import { useCallback, useEffect, useRef, useState } from 'react';
import { scoreRun } from '@continuum/show-core';
import type { TaskDef, TaskState } from '@continuum/show-core';
import { GhostButton, PrimaryButton, mono, sans } from '../ui/atoms.js';
import { SCORE_COPY } from '../ui/scoreText.js';
import type { Lang } from '../ui/text.js';
import type { ScreenCopy } from '../ui/screenText.js';
import { useGameClock } from './clock.js';
import type { RunTally } from './clock.js';
import { Thread } from './Thread.js';
import { Rhythm } from './Rhythm.js';
import { Vine } from './Vine.js';
import { Echo } from './Echo.js';
import { Recall } from './Recall.js';
import { Divide } from './Divide.js';
import { Forage } from './Forage.js';
import { Relay } from './Relay.js';
import { ObserverView } from './Observer.js';
import { HowToCard } from './HowTo.js';
import { FitToScreen } from '../ui/FitToScreen.js';
import { useGameUnderWay } from '../screens/GameScreen.js';
import { eraOf } from './art.js';
import { ThirtyGameView } from './thirty/kit.js';
import type { MirrorFrame } from './thirty/kit.js';
import { thirtyBoard } from './thirty/index.js';

/** Wrong moves allowed in one round. The next one ends it with TRY AGAIN (five since 2026-09-29). */
export const MISTAKES_ALLOWED = 5;

export interface StationProps {
  def: TaskDef;
  /** Drives the era the board is drawn in. */
  chapter: number;
  task: TaskState | undefined;
  accent: string;
  /** Set when watching someone else — the screen goes read-only. */
  observing: { callsign: string; seat: number } | null;
  /** How many crew are looking over your shoulder. */
  watchers: number;
  onProgress: (progress: number) => void;
  onFrame: (frame: string) => void;
  /** WELL DONE, with the time on screen and the mistakes, across every TRY AGAIN. */
  onComplete: (run: RunTally | undefined) => void;
  onClose: () => void;
  /** Stop watching and come back to your own screen. */
  onStopWatching: () => void;
  /**
   * Where this game's stopwatch is kept (clock.ts), or null when there is nothing to time:
   * someone else's game, or one already finished.
   */
  clockKey: string | null;
  /** Something covers the game (an alarm card, a sheet, a meeting): the game and its stopwatch wait. */
  paused?: boolean;
  lang?: Lang;
  s: ScreenCopy;
  /** Call sign for a seat, for the one game that names another guest. */
  nameOf: (seat: number) => string | null;
  /**
   * Hand a game of the thirty's board out as it is played, a few times a second, for a screen that
   * mirrors it (thirty/kit/mirror.ts). Off unless asked for.
   */
  mirror?: boolean;
  onMirror?: ((frame: MirrorFrame) => void) | undefined;
  /** The practice page's still of a how-to demo: this many ms into its loop. */
  demoAt?: number | undefined;
}

/**
 * One station: your own game, or a live look at somebody else's.
 *
 * In observer mode the whole thing is greyed and captioned, because it has to be
 * unmistakable that you cannot touch it — a guest poking a dead screen is worse than
 * no observer mode at all.
 */
export function Station({
  def,
  chapter,
  task,
  accent,
  observing,
  watchers,
  onProgress,
  onFrame,
  onComplete,
  onClose,
  onStopWatching,
  clockKey,
  paused = false,
  lang = 'en',
  s: t,
  nameOf,
  mirror = false,
  onMirror,
  demoAt,
}: StationProps) {
  // Frames ride the 10 Hz patch, so they are throttled hard on the way out.
  const lastFrame = useRef(0);
  const sendFrame = useCallback(
    (frame: string) => {
      const now = Date.now();
      if (now - lastFrame.current < 180) return;
      lastFrame.current = now;
      onFrame(frame);
    },
    [onFrame],
  );

  // The how-to card first, and the game only once START is pressed. RELAY needs no card: its
  // board is the instruction.
  const [started, setStarted] = useState(def.type === 'RELAY');
  // Under way: a short phone's sheet folds its instruction line away for the board.
  useGameUnderWay(started);
  // A round ends one of two ways: WELL DONE, or one mistake too many and TRY AGAIN.
  const [round, setRound] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [result, setResult] = useState<'won' | 'lost' | null>(null);
  /**
   * The round's end, settled at once: a game can report its last mistake and a win in the same
   * moment (a clipped last gate), and only the first of the two may count.
   */
  const over = useRef(false);
  const slips = useRef(0);
  // The score: time on this screen and every mistake, carried across TRY AGAIN. The dots below
  // count this round only; the clock counts them all.
  const clock = useGameClock(observing ? null : clockKey, paused || !started);
  const [scored, setScored] = useState<{ run: RunTally; points: number } | null>(null);
  const s = SCORE_COPY[lang];
  // WELL DONE or TRY AGAIN is a decision: bring its buttons into view on a short phone.
  const resultRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [result]);

  if (observing) {
    return (
      <div style={{ position: 'relative' }}>
        <div
          style={{ filter: 'grayscale(.72) brightness(.72)', pointerEvents: 'none', opacity: 0.9 }}
        >
          <ObserverView def={def} task={task} accent={accent} era={eraOf(chapter)} title={t.livePlaying(observing.callsign)} />
        </div>

        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            border: '1px solid rgba(255,255,255,.16)',
            borderRadius: 10,
            padding: '9px 12px',
            background: 'rgba(255,255,255,.04)',
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: '#8fa4b5',
              animation: 'ccPulse 1.8s infinite',
            }}
          />
          <span style={{ fontFamily: mono, fontSize: 12, letterSpacing: 1, lineHeight: 1.4, color: '#b8c6d2' }}>
            {t.observerNote(observing.callsign)}
          </span>
        </div>

        <button
          onClick={onStopWatching}
          style={{
            marginTop: 10,
            width: '100%',
            minHeight: 50,
            borderRadius: 11,
            border: '1px solid rgba(255,255,255,.22)',
            background: 'transparent',
            color: '#dfe9f2',
            fontFamily: sans,
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: 2.5,
          }}
        >
          {t.stopWatching}
        </button>
      </div>
    );
  }

  const mistake = () => {
    if (over.current) return;
    clock?.mistake();
    slips.current += 1;
    setMistakes(slips.current);
    if (slips.current > MISTAKES_ALLOWED) {
      over.current = true;
      setResult('lost');
    }
  };
  const win = () => {
    if (over.current) return;
    over.current = true;
    const run = clock?.read(Date.now());
    // The same sum the room does, so the number on WELL DONE is the one on the TV.
    if (run) {
      const room = scoreRun(def.parMs, run.ms, run.mistakes);
      setScored({ run: { ms: room.ms ?? run.ms, mistakes: room.mistakes ?? run.mistakes }, points: room.points });
    }
    setResult('won');
    onComplete(run);
  };
  const again = () => {
    over.current = false;
    slips.current = 0;
    setRound((r) => r + 1);
    setMistakes(0);
    setResult(null);
    onProgress(0);
  };
  const props = { accent, era: eraOf(chapter), onProgress, onWin: win, onMistake: mistake, paused: paused || result !== null, s: t };
  // Games where nothing can go wrong show no mistake count.
  const counted = def.type !== 'RELAY' && def.type !== 'FORAGE' && !(def.type === 'PLAY' && thirtyBoard(def.game)?.mistakes === false);

  if (!started) {
    // The card and its START on one screen, however short the phone (scaled to fit, never scrolled to).
    return (
      <FitToScreen>
        <div style={{ position: 'relative' }}>
          <HowToCard def={def} accent={accent} s={t} lang={lang} era={eraOf(chapter)} demoAt={demoAt} onStart={() => setStarted(true)} />
          {/* On a short screen the header's ✕ closes, and the card keeps its size instead. */}
          <button
            className="cc-extra-close"
            onClick={onClose}
            style={{
              marginTop: 12,
              width: '100%',
              minHeight: 48,
              borderRadius: 10,
              border: '1px solid rgba(255,255,255,.16)',
              background: 'transparent',
              color: '#9db3c2',
              fontFamily: mono,
              fontSize: 12,
              letterSpacing: 2,
            }}
          >
            {t.close}
          </button>
        </div>
      </FitToScreen>
    );
  }
  /** The result's buttons: one big thing to press, and LATER visibly smaller below it. */
  const actions = { marginTop: 6, width: '100%', maxWidth: 320, display: 'flex', flexDirection: 'column', gap: 10 } as const;

  return (
    <div style={{ position: 'relative' }}>
      {watchers > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: -6 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontFamily: mono,
              fontSize: 11,
              letterSpacing: 1,
              color: accent,
              border: `1px solid ${accent}55`,
              borderRadius: 12,
              padding: '3px 8px',
              background: `${accent}14`,
            }}
          >
            {t.watchers(watchers)}
          </span>
        </div>
      )}

      <div
        key={round}
        style={{ pointerEvents: result ? 'none' : undefined, opacity: result ? 0.18 : 1, transition: 'opacity .3s ease' }}
      >
        {def.type === 'THREAD' && <Thread task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'RHYTHM' && <Rhythm task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'VINE' && <Vine task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'ECHO' && <Echo task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'RECALL' && <Recall task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'DIVIDE' && <Divide task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'FORAGE' && <Forage task={def} {...props} onFrame={sendFrame} />}
        {def.type === 'RELAY' && <Relay task={def} relay={task?.relay} nameOf={nameOf} {...props} />}
        {def.type === 'PLAY' && thirtyBoard(def.game) && (
          <ThirtyGameView task={def} game={thirtyBoard(def.game)!} lang={lang} {...props} onFrame={sendFrame} mirror={mirror} onMirror={onMirror} />
        )}
      </div>

      {counted && !result && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 6,
            fontFamily: mono,
            fontSize: 12,
            letterSpacing: 1,
            color: '#9db3c2',
          }}
        >
          {t.mistakes(MISTAKES_ALLOWED)}
          {Array.from({ length: MISTAKES_ALLOWED }, (_, i) => (
            <span
              key={i}
              style={{
                width: 9,
                height: 9,
                borderRadius: '50%',
                background: i < mistakes ? '#ff5f86' : 'transparent',
                border: `1px solid ${i < mistakes ? '#ff5f86' : 'rgba(255,255,255,.3)'}`,
              }}
            />
          ))}
        </div>
      )}

      {paused && !result && (
        <div
          aria-live="polite"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            zIndex: 4,
            borderRadius: 14,
            background: '#05070a',
            fontFamily: sans,
            fontSize: 26,
            fontWeight: 700,
            letterSpacing: 3,
            color: '#9db3c2',
          }}
        >
          {t.paused}
        </div>
      )}

      {result && (
        <div
          ref={resultRef}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            // From the top, so the whole decision fits on a short phone once scrolled to.
            justifyContent: 'flex-start',
            gap: 8,
            padding: '22px 16px 16px',
            textAlign: 'center',
            zIndex: 5,
            borderRadius: 14,
            background: '#05070a',
            animation: 'ccRise .25s ease-out',
          }}
        >
          <div
            style={{
              fontFamily: sans,
              fontSize: 34,
              fontWeight: 700,
              letterSpacing: 3,
              lineHeight: 1.1,
              color: result === 'won' ? accent : '#ff5f86',
            }}
          >
            {result === 'won' ? t.wellDone : t.tryAgainTitle}
          </div>
          {result === 'won' && scored && (
            <div>
              <div style={{ fontFamily: sans, fontSize: 26, fontWeight: 700, letterSpacing: 2, lineHeight: 1.1, color: '#dfe9f2' }}>
                {s.points(scored.points)}
              </div>
              <div style={{ marginTop: 2, fontFamily: mono, fontSize: 12, letterSpacing: 0.5, color: '#9db3c2' }}>
                {s.runLine(scored.run.ms, def.parMs, scored.run.mistakes)}
              </div>
            </div>
          )}
          <div style={{ fontFamily: sans, fontSize: 17, lineHeight: 1.3, color: '#dfe9f2', maxWidth: 300 }}>
            {result === 'won' ? t.wonText : t.lostText(MISTAKES_ALLOWED + 1)}
          </div>
          {result === 'won' ? (
            <div style={actions}>
              <PrimaryButton big bg={accent} fg="#05070a" onClick={onClose}>
                {t.done}
              </PrimaryButton>
            </div>
          ) : (
            <div style={actions}>
              <PrimaryButton big bg={accent} fg="#05070a" onClick={again}>
                {t.tryAgainTitle}
              </PrimaryButton>
              <GhostButton color="#9db3c2" border="rgba(255,255,255,.2)" onClick={onClose}>
                {t.later}
              </GhostButton>
            </div>
          )}
        </div>
      )}

      {/* On a short screen the header's ✕ closes the game, and the board has this room instead. */}
      <button
        className="cc-extra-close"
        onClick={onClose}
        style={{
          marginTop: 12,
          width: '100%',
          minHeight: 48,
          borderRadius: 10,
          border: '1px solid rgba(255,255,255,.16)',
          background: 'transparent',
          color: '#9db3c2',
          fontFamily: mono,
          fontSize: 12,
          letterSpacing: 2,
        }}
      >
        {t.close}
      </button>
    </div>
  );
}
